import React, { FC, useTransition } from 'react';
import { Flex, Table, Text } from '@mantine/core';
import classNames from 'classnames';
import { IReportedSiteUser, putSiteUsersStatus, useAppDispatch } from 'src/store';
import { ReactComponent as Link } from 'src/assets/svg/link-2.svg';
import { ReactComponent as ExtUser } from 'src/assets/svg/ext_user.svg';
import { ReactComponent as WebOwner } from 'src/assets/svg/web_owner.svg';

import styles from './styles.module.scss';
import { Switch } from '@common/components';
import { useTranslation } from 'react-i18next';
import SiteLink from '../SiteLink';

interface TableReportProps {
  report: TableObject[];
}

interface TableObject {
  tenantId: string;
  userName: string;
  userType: string;
  planType: string;
  websites: string[];
  active: boolean;
  email: string;
}

interface UserListProps {
  report: IReportedSiteUser[];
}

const UserList: FC<UserListProps> = ({ report }) => {
  const newData = report?.flatMap(({ tenant, users, websites, isActive }) => {
    return users.map((user) => ({
      tenantId: tenant.tenantId,
      userName: (user.firstName || user.lastName) && user.firstName + ' ' + user.lastName,
      userType: user.isWebsiteOwner ? 'Website Owner' : user.isWidgetUser ? 'Widget User' : 'Undefined User',
      planType: tenant.paymentPlan,
      websites: websites,
      active: isActive,
      email: user.email,
    }));
  });
  return (
    <Flex direction={{ base: 'column' }}>
      <UserTable report={newData}></UserTable>
    </Flex>
  );
};

const UserTable: FC<TableReportProps> = ({ report }) => {
  const dispatch = useAppDispatch();
  const [t] = useTranslation('users');
  return (
    <Table className={styles.table}>
      <thead className={styles['table-header']}>
        <tr>
          <th>{t('userName')}</th>
          <th>{t('userType')}</th>
          <th>{t('planType')}</th>
          <th>{t('email')}</th>
          <th>{t('websites')}</th>
          <th>{t('status')}</th>
        </tr>
      </thead>
      <tbody>
        {report.map((item, index) => (
          <tr key={index} className={classNames(styles['table-row'], styles['vertical-margin'])}>
            <td className={classNames(styles['table-cell'], styles['user-name'])}>{item.userName}</td>
            <td className={styles['table-cell']}>
              {
                <Flex gap={10}>
                  {item.userType === 'Website Owner' && <WebOwner aria-hidden="true" />}
                  {item.userType === 'Widget User' && <ExtUser aria-hidden="true" />}
                  <Text>{item.userType}</Text>
                </Flex>
              }
            </td>
            <td className={classNames(styles['table-cell'], styles['user-type'])}>
              {item.planType[0].toUpperCase() + item.planType.slice(1)}
            </td>
            <td className={styles['table-cell']}>{item.email}</td>
            <td className={styles['table-cell']}>
              {item.websites.map((site) => (
                <SiteLink key={site} website={site} />
              ))}
            </td>
            <td className={styles['table-cell']}>
              <Switch
                checked={item.active}
                labelPosition={'left'}
                onChecked={() => {
                  dispatch(putSiteUsersStatus({ tenantId: item.tenantId, data: { active: !item.active } }));
                }}
                label={'Active'}
              />{' '}
            </td>
          </tr>
        ))}
      </tbody>
    </Table>
  );
};

export default UserList;
